import Builder from './xpath-builder';

/**
 * Helper function to get a key name
 * @param {*} variable - an object containing a single key and value
 * @return the key as a string
 *
 */
function varName(variable) {
    return Object.keys(variable)[0];
}

/**
 * Takes a key passed in the JSON payload from the front end
 * and returns an object with the attribute name and a
 * builderFunction or a string (the attribute name).
 *
 * @param {String} key - The key from the request data (the form field name).
 * @return the attribute object associated with the input key
 *
 * ```js
 * requestFieldMapper("resourceName") // => {attribute: "ResourceName", resourceNameBuilder}
 * requestFieldMapper("resourceName_lang") // => "lang"
 * ```
 */
export function requestFieldMapper(key) {
    try {
        // eslint-disable-next-line
        return requestFieldMap[key];
    } catch (error) {
        throw new Error(
            `This attribute wasn't found in the DTO map: ${key}`,
            error,
        );
    }
}

/**
 * Attribute Object that contains information on the attribute,
 * such as the attribute name, xpath builder function, etc.
 * @param {*} value
 * @return
 */
function getAttrObj(attribute, builderFunction) {
    return {attribute, builderFunction};
}

/**
 * Builder function specifically made for the ResourceName &
 * AlternateResourceName attributes.
 * @param {String} attribute
 * @param {String} value
 */
const resourceNameBuilder = ({attribute, value}) => {
    //console.log('resourceNameAttri: ', attribute);
    // console.log('resourceNameValue: ', value);
    const {
        resourceName,
        exactResourceNameMatch,
        alternateResourceNameAddition,
        lang,
        titleClass,
        alternateId,
    } = value;

    let orExpressions = [];
    const builder = new Builder();

    const altRNAttr = requestFieldMapper(
        varName({alternateResourceNameAddition}),
    );

    if (exactResourceNameMatch) {
        // create xpath for exact match
        orExpressions.push(builder.fullBase().is(attribute, resourceName).xpath);

        if (alternateResourceNameAddition) {
            // create xpath for alt ID exact match
            orExpressions.push(builder.fullBase().is(altRNAttr, resourceName).xpath);
        }
    } else {
        // create xpath for tokenized match
        orExpressions.push(
            builder.fullBase().multiMatch(attribute, resourceName).wrap().xpath,
        );
    }

    // join all of the xpaths created & return if language and titleClass were omitted
    let {xpath} = builder.or(...orExpressions).wrap();
    if (!lang && !titleClass) return xpath;

    // helper function to add @lang & @titleClass xpaths
    const andExpressions = [xpath];
    const propHelper = (property) => {
        const [propKey, propValue] = Object.entries(property)[0];
        const propName = requestFieldMapper(propKey);

        if (propKey && propValue && propName) {
            orExpressions = [];
            orExpressions.push(
                builder.fullBase().multiMatch(attribute, propValue, propName).xpath,
            );

            if (alternateResourceNameAddition) {
                orExpressions.push(
                    builder.fullBase().multiMatch(altRNAttr, propValue, propName).xpath,
                );
            }

            andExpressions.push(builder.or(...orExpressions).wrap().xpath);
        }
    };

    // check if language, titleClass were included, create xpaths for them
    propHelper({lang});
    propHelper({titleClass});
    // TODO: check for resource name systemGenerated

    // join all xpaths and return
    xpath = builder.and(...andExpressions).xpath;

    return xpath;
};

/**
 * Determines if record is root title record and creates xpath filter on query search
 * @return ((NOT ((/FullMetadata/ExtraObjectMetadata/SeasonInfo EXISTS)
 *              OR (/FullMetadata/ExtraObjectMetadata/ClipInfo EXISTS)
 *              OR (/FullMetadata/ExtraObjectMetadata/ManifestationInfo EXISTS)
 *              OR (/FullMetadata/ExtraObjectMetadata/SeriesInfo EXISTS)
 *              OR (/FullMetadata/ExtraObjectMetadata/EpisodeInfo EXISTS)
 *              OR (/FullMetadata/ExtraObjectMetadata/EditInfo EXISTS)
 *              OR (/FullMetadata/ExtraObjectMetadata/CompilationInfo EXISTS)
 *          ))
 */
const rootTitleRecordBuilder = () => {
    const orExpressions = [];
    const builder = new Builder();
    // TODO move to filters config
    [
        'SeasonInfo',
        'ClipInfo',
        'ManifestationInfo',
        'SeriesInfo',
        'EpisodeInfo',
        'EditInfo',
        'CompilationInfo',
    ].forEach((attr) => {
        orExpressions.push(builder.fullExtra().append(attr).exists().xpath);
    });

    return builder
        .or(...orExpressions)
        .wrap()
        .not()
        .wrap().xpath;
};

/**
 * Returns a callback that will return an xpath for series
 *
 * @return xpath for series:
 * ((/FullMetadata/ExtraObjectMetadata/CompilationInfo EXISTS AND
 * /FullMetadata/ExtraObjectMetadata/CompilationInfo/CompilationClass "Series") OR
 * /FullMetadata/BaseObjectData/ReferentType "Season")
 */
function seriesBuilder() {
    let xpath = '';

    const andExpressions = [];
    const orExpressions = [];
    const builder = new Builder();

    // build compilationInfo EXISTS part of xpath
    const compilationInfoExists = builder
        .fullExtra()
        .append('CompilationInfo')
        .exists();
    //andExpressions.push(compilationInfoExists.xpath);

    // build compilationClass Series part of xpath
    const compilationClassSeries = builder
        .fullExtra()
        .append('SeriesInfo').exists();
    //.match('CompilationClass', 'Series');
    andExpressions.push(compilationClassSeries.xpath);

    // join with AND
    xpath = builder.and(...andExpressions).wrap().xpath;
    orExpressions.push(xpath);

    // build referentType Season part of xpath
    const referentTypeSeason = builder.fullBase().match('ReferentType', 'Season');
    //orExpressions.push(referentTypeSeason.xpath);

    // join with OR
    xpath = builder.or(...orExpressions).wrap().xpath;
    return xpath;
}

/**
 *  Returns a callback that will return an xpath for edits and clips
 *
 * @return xpath for editsClips
 * (/FullMetadata/ExtraObjectMetadata/EditInfo EXISTS
 * AND /FullMetadata/ExtraObjectMetadata/ClipInfo EXISTS)
 */
function editsClipsBuilder() {
    let xpath = '';

    const andExpressions = [];
    const builder = new Builder();

    andExpressions.push(builder.fullExtra().append('EditInfo').exists().xpath);
    andExpressions.push(builder.fullExtra().append('ClipInfo').exists().xpath);
    //console.log('editsAndExpressions: ', andExpressions);

    // join expressions with AND
    xpath = builder.and(...andExpressions).wrap().xpath;
    return xpath;
}

/**
 * Returns callback return xpath for manifestations
 * @return /FullMetadata/ExtraObjectMetadata/ManifestationInfo EXISTS
 */
function manifestationsBuilder() {
    let xpath = '';

    const builder = new Builder();
    xpath = builder.fullExtra().append('ManifestationInfo').exists().xpath;

    return xpath;
}

/**
 * Episodes builder function returns the xpath for Episodes filter
 * in the EIDR XML queries.
 * @return /FullMetadata/ExtraObjectMetadata/EpisodeInfo Exists OR
 *  (/FullMetadata/ExtraObjectMetadata/EditInfo Exists
 *  AND /FullMetadata/ExtraObjectMetadata/EditInfo/EditClass "Split")
 */
function episodesBuilder() {
    let xpath = '';
    const andExpressions = [];
    const orExpressions = [];

    const builder = new Builder();
    const episodesInfoExistsXPath = builder
        .fullExtra()
        .append('EpisodeInfo')
        .exists();
    orExpressions.push(episodesInfoExistsXPath.xpath);

    const episodesEditInfoExistsXPath = builder
        .fullExtra()
        .append('EditInfo')
        .exists();
    andExpressions.push(episodesEditInfoExistsXPath.xpath);

    const episodesSplitXPath = builder
        .fullExtra()
        .append('EditInfo')
        .match('EditClass', 'Split');
    andExpressions.push(episodesSplitXPath.xpath);

    // join both AND expressions first and then join first and last two with an OR
    orExpressions.push(builder.and(...andExpressions).wrap().xpath);

    xpath = builder.or(...orExpressions).wrap().xpath;
    return xpath;
}

/**
 * Compilation Builder function returns xpath for Compilation check
 * @return /FullMetadata/ExtraObjectMetadata/CompilationInfo EXISTS
 */
const compilationBuilder = () => {
    const builder = new Builder();
    return builder.fullExtra().append('CompilationInfo').exists().xpath;
};

/**
 * Returns callback return xpath for seasons
 *
 * @return xpath for seasons
 * ((/FullMetadata/ExtraObjectMetadata/CompilationInfo EXISTS
 * AND /FullMetadata/ExtraObjectMetadata/CompilationInfo/CompilationClass "Season")
 * OR /FullMetadata/BaseObjectData/ReferentType "Season")
 */
function seasonsBuilder() {
    let xpath = '';

    const andExpressions = [];
    const orExpressions = [];
    const builder = new Builder();

    const compilationInfoExists = builder
        .fullExtra()
        .append('CompilationInfo')
        .exists();
    andExpressions.push(compilationInfoExists.xpath);

    const compilationClassSeasons = builder
        .fullExtra()
        .append('CompilationInfo')
        .match('CompilationClass', 'Season');
    andExpressions.push(compilationClassSeasons.xpath);

    xpath = builder.and(...andExpressions).wrap().xpath;
    orExpressions.push(xpath);

    const referentTypeSeason = builder.fullBase().match('ReferentType', 'Season');
    orExpressions.push(referentTypeSeason.xpath);

    xpath = builder.or(...orExpressions).wrap().xpath;
    return xpath;
}

/**
 *
 * @param {*} param0
 */
const filterBuilder = ({attribute, value}) => {
    if (Object.entries(value).length === 0) {
        return '';
    }

    let xpath = '';
    const orExpressions = [];

    Object.entries(value).forEach((key) => {
        switch (key[0]) {
            case 'rootTitleRecords':
                orExpressions.push(rootTitleRecordBuilder());
                break;
            case 'compilations':
                orExpressions.push(compilationBuilder());
                break;
            case 'series':
                orExpressions.push(seriesBuilder());
                break;
            case 'editsClips':
                orExpressions.push(editsClipsBuilder());
                break;
            case 'manifestations':
                orExpressions.push(manifestationsBuilder());
                break;
            case 'seasons':
                orExpressions.push(seasonsBuilder());
                break;
            case 'episodes':
                orExpressions.push(episodesBuilder());
                break;
            default:
                console.error('Unsupported filter type: ', key[0]);
        }
    });

    // create xpath for alt id tokenized match
    if (attribute.alternateResourceNameAddition) {
        const builder = new Builder();
        orExpressions.push(
            builder.fullBase().multiMatch(altRNAttr, resourceName).wrap().xpath,
        );
    }

    const builder = new Builder();
    xpath = builder.or(...orExpressions).wrap().xpath;
    //console.log('final filter xpath: ', xpath);
    return xpath;
};

/**
 * Default builder function returns the most common XML path
 * in the EIDR XML queries.
 * @param {String} attribute - the attribute name as accessed in the registry
 * @param {value} value - the value assoicated the attribute from the request
 * @return /FullMetadata/BaseObjectMetadata/<attribute> '<value>'
 */
function defaultBuilder({attribute, value}) {
    let xpath = '';
    if (value) {
        const builder = new Builder();
        xpath = builder.fullBase().match(attribute, value).xpath;
    }
    return xpath;
}

/**
 * Returns a callback that will wrap an xpath query in ASCII.
 * If the value passed with ASCII is explicitly false, nothing
 * will be returned.
 * @param {String} expression
 * @return a callback function that will wrap the xpath in the
 * ASCII wrapper.
 */
function ASCIIBuilder({value}) {
    if (value) {
        return ({xpath}) => {
            const builder = new Builder(xpath);
            return builder.wrap('ASCII').xpath;
        };
    }
}

/**
 * Returns a callback that will return an xpath for releaseDateFrom input
 * @param {String} attribute
 * @param {String} value
 * @return xpath for releaseDateFrom: /FullMetadata/BaseObjectData/ReleaseDate >= '<value>'
 */
function releaseDateFromBuilder({attribute, value}) {
    let xpath = '';

    if (value) {
        const andExpressions = [];
        const builder = new Builder();
        andExpressions.push(builder.fullBase().gte(attribute, value).xpath);
        xpath = builder.and(...andExpressions).xpath;
    }

    return xpath;
}

/**
 * Returns a callback that will return an xpath for releaseDateTo input
 * @param {String} attribute
 * @param {String} value
 * @return xpath for releaseDateTo: /FullMetadata/BaseObjectData/ReleaseDate <= '<value>'
 */
function releaseDateToBuilder({attribute, value}) {
    let xpath = '';

    if (value) {
        const andExpressions = [];
        const builder = new Builder();
        andExpressions.push(builder.fullBase().lte(attribute, value).xpath);
        xpath = builder.and(...andExpressions).xpath;
    }

    return xpath;
}

/*
 * Returns a builder function made to handle multiselect filter options, such as country of origin and original language.
 * @params {*} value
 * @return /FullMetadata/BaseObjectData/<attribute> '<value1>' OR /FullMetadata/BaseObjectData/<attribute> '<value2>' OR ...
 */
function multiSelectBuilder({attribute, value}) {
    let xpath = '';
    if (value && !Array.isArray(value)) {
        value = [value];
    }
    if (value && value.length > 0) {
        const orExpressions = [];
        const builder = new Builder();
        value.forEach((v) => {
            orExpressions.push(builder.fullBase().match(attribute, v).xpath);
        });
        xpath = builder.or(...orExpressions).wrap().xpath;
    }
    return xpath;
}

const requestFieldMap = {
    // ?: Should every value be attribute objects? (Some are currently string)
    // ?: Should there should be a validation field to check the type of the attribute value?
    resourceName: getAttrObj('ResourceName', resourceNameBuilder),
    customAsciiSearch: getAttrObj('CustomAsciiSearch', ASCIIBuilder),
    status: getAttrObj('Status', defaultBuilder),
    referentType: getAttrObj('ReferentType', multiSelectBuilder),
    id: getAttrObj('ID', defaultBuilder),
    alternateResourceNameAddition: 'AlternateResourceName',
    lang: 'lang',
    titleClass: 'titleClass',
    originalLanguages: getAttrObj('OriginalLanguage', multiSelectBuilder),
    countryOfOrigin: getAttrObj('CountryOfOrigin', multiSelectBuilder),
    releaseDateFrom: getAttrObj('ReleaseDate', releaseDateFromBuilder),
    releaseDateTo: getAttrObj('ReleaseDate', releaseDateToBuilder),
    filters: getAttrObj('Filters', filterBuilder),
    extraObjectMetadata: getAttrObj('ExtraObjectMetadata', defaultBuilder),
};
